import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { explicarErroDoBrain } from '@/data/brain';
import type { IdiomaDoBrain, PropostaDoBrain } from '@/data/brain';
import { dados } from '@/data/porta';

import { falaDoDesfecho, paraAcaoPendente, propostasPendentes, textoDoFeito } from './acaoPendente';
import type { PendingAction } from './acaoPendente';
import { intencaoDaIa, intencaoSobreAcao } from './interrupcao';
import type { FaseDaVoz } from './useConversaPorVoz';

/**
 * As decisões dentro da conversa por voz (B9, decisão do usuário em 13/09/2026).
 *
 * A VecchIA propõe; o cartão aparece SOBRE a tela de voz, sem fechá-la nem pausar a fala; a
 * pessoa decide tocando ou falando. Botão e voz caem no MESMO `decidir(id)`: uma trava por id
 * impede o segundo envio, e o servidor ainda recusa a proposta já decidida. Regras:
 * - uma decisão por vez: as outras esperam na fila, e a próxima só aparece depois de a VecchIA
 *   terminar de falar o desfecho da anterior;
 * - fala que começou antes de o cartão aparecer não decide ele (resposta antiga nunca confirma
 *   ação nova);
 * - o que está claro ("pode", "agora não") resolve na hora (`intencaoSobreAcao`); o que fica em
 *   dúvida vai ao modelo rápido (`brainTexto('intencao')`), com a ação como contexto; o resto é
 *   conversa e segue para a VecchIA;
 * - o desfecho fica à vista um instante, e "Continuar" (`dispensar`) o tira na hora;
 * - somente leitura (impersonação): o cartão mostra, mas nada decide.
 */

export type EstadoDaInteracao = 'aguardando' | 'executando' | 'feita' | 'falhou' | 'cancelada';

export interface InteracaoDaVoz {
    acao: PendingAction;
    estado: EstadoDaInteracao;
    /** Enquanto executa: se foi confirmar ou cancelar. */
    decisao: 'confirmar' | 'cancelar' | null;
    /** No desfecho: o que foi feito, o motivo ou o erro. */
    mensagem: string;
    /** Na fila desta conversa: 1 de 2. */
    posicao: number;
    total: number;
    /** O cartão já está à vista. */
    visivel: boolean;
    /** Saindo de cena (a animação de saída). */
    saindo: boolean;
}

export interface AcoesDaVoz {
    atual: InteracaoDaVoz | null;
    confirmar: () => void;
    cancelar: () => void;
    /** Tira o desfecho da tela na hora ("Continuar"). */
    dispensar: () => void;
    /** A frase decide a ação em foco? Devolve se decidiu — aí ela não vira mensagem. */
    resolverFala: (texto: string, contexto: { comecouEm: number }) => Promise<boolean>;
}

export interface OpcoesDasAcoesDaVoz {
    ativa: boolean;
    mensagens: ReadonlyArray<{ id: string; propostas: PropostaDoBrain[] }>;
    fase: FaseDaVoz;
    somenteLeitura?: string | null;
    idioma: IdiomaDoBrain;
    aoMudarProposta: (mensagemId: string, nova: PropostaDoBrain) => void;
    anunciar: (texto: string) => Promise<void>;
}

/** Com a VecchIA falando, o cartão entra um pouco depois, sem cortar nada. */
const ATRASO_DURANTE_A_FALA_MS = 900;
/** O desfecho fica à vista este tanto antes de sair sozinho. */
const DESFECHO_A_VISTA_MS = 2600;
const SAIDA_MS = 240;
/** Entre uma decisão e a próxima da fila. */
const INTERVALO_ENTRE_DECISOES_MS = 700;
/** O modelo rápido tem este tanto para dizer se a frase decide; depois, é conversa. */
const LIMITE_DA_IA_MS = 4000;

interface EmFoco {
    id: string;
    mensagemId: string;
    acao: PendingAction;
    estado: EstadoDaInteracao;
    decisao: 'confirmar' | 'cancelar' | null;
    mensagem: string;
    visivelDesde: number;
    saindo: boolean;
}

function esperar(ms: number): Promise<void> {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
}

async function perguntarAoModeloRapido(texto: string, acao: PendingAction, idioma: IdiomaDoBrain): Promise<'confirmar' | 'cancelar' | 'outra'> {
    const pedido = [
        `Ação esperando decisão: ${acao.title}${acao.description ? ` — ${acao.description}` : ''}`,
        ...(acao.details ?? []).map((d) => `${d.label}: ${d.value}`),
        `Fala da pessoa: "${texto}"`,
    ].join('\n');
    try {
        const resposta = await Promise.race([dados().brainTexto('intencao', pedido, idioma), esperar(LIMITE_DA_IA_MS).then(() => '')]);
        return intencaoDaIa(resposta);
    } catch {
        return 'outra';
    }
}

export function useAcoesDaVoz({ ativa, mensagens, fase, somenteLeitura, idioma, aoMudarProposta, anunciar }: OpcoesDasAcoesDaVoz): AcoesDaVoz {
    const [emFoco, setEmFoco] = useState<EmFoco | null>(null);
    const [agora, setAgora] = useState(() => Date.now());
    const emFocoRef = useRef<EmFoco | null>(null);
    emFocoRef.current = emFoco;
    const decididas = useRef(new Set<string>());
    const jaDecidiu = useRef(false);
    const opcoes = useRef({ aoMudarProposta, anunciar, idioma, somenteLeitura });
    opcoes.current = { aoMudarProposta, anunciar, idioma, somenteLeitura };

    const pendentes = propostasPendentes(mensagens, decididas.current);
    const idsPendentes = pendentes.map((p) => p.proposta.id).join('|');

    /* Escolhe a próxima decisão da fila; fecha a atual se ela saiu da fila por fora (confirmada no chat, expirada). */
    useEffect(() => {
        if (!ativa) {
            setEmFoco(null);
            return;
        }
        if (emFoco) {
            if (emFoco.estado === 'aguardando' && !emFoco.saindo && !pendentes.some((p) => p.proposta.id === emFoco.id)) setEmFoco(null);
            return;
        }
        const proxima = pendentes[0];
        if (!proxima) return;
        /* Depois de uma decisão, a próxima espera a VecchIA terminar de falar o desfecho. */
        if (jaDecidiu.current && fase === 'speaking') return;
        const atraso = fase === 'speaking' ? ATRASO_DURANTE_A_FALA_MS : jaDecidiu.current ? INTERVALO_ENTRE_DECISOES_MS : 0;
        setEmFoco({
            id: proxima.proposta.id, mensagemId: proxima.mensagemId, acao: paraAcaoPendente(proxima.proposta),
            estado: 'aguardando', decisao: null, mensagem: '', visivelDesde: Date.now() + atraso, saindo: false,
        });
        // `pendentes` muda de identidade a cada render; `idsPendentes` diz quando a fila mudou de fato.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ativa, emFoco, fase, idsPendentes]);

    /* O cartão aparece quando chega a hora dele. */
    useEffect(() => {
        if (!emFoco || emFoco.visivelDesde <= agora) return undefined;
        const timer = setTimeout(() => setAgora(Date.now()), Math.max(0, emFoco.visivelDesde - Date.now()) + 10);
        return () => clearTimeout(timer);
    }, [emFoco, agora]);

    /** O cartão sai de cena (animação) e abre espaço para a próxima decisão. */
    const tirarDeCena = useCallback(async (id: string) => {
        setEmFoco((atual) => (atual && atual.id === id && !atual.saindo ? { ...atual, saindo: true } : atual));
        await esperar(SAIDA_MS);
        jaDecidiu.current = true;
        setEmFoco((atual) => (atual && atual.id === id ? null : atual));
    }, []);

    const decidir = useCallback(async (id: string, decisao: 'confirmar' | 'cancelar') => {
        const alvo = emFocoRef.current;
        if (!alvo || alvo.id !== id || alvo.estado !== 'aguardando' || alvo.saindo) return;
        if (opcoes.current.somenteLeitura || decididas.current.has(id)) return;
        /* A trava: o segundo toque, ou a voz junto com o toque, encontra o id já decidido. */
        decididas.current.add(id);
        const mudar = (parcial: Partial<EmFoco>) => setEmFoco((atual) => (atual && atual.id === id ? { ...atual, ...parcial } : atual));
        mudar({ estado: 'executando', decisao });

        let fala: string;
        try {
            const nova = decisao === 'confirmar' ? await dados().brainConfirmarProposta(id) : await dados().brainCancelarProposta(id);
            opcoes.current.aoMudarProposta(alvo.mensagemId, nova);
            if (nova.situacao === 'confirmada') mudar({ estado: 'feita', mensagem: textoDoFeito(nova.ferramenta) });
            else if (nova.situacao === 'cancelada') mudar({ estado: 'cancelada', mensagem: 'Nada foi alterado.' });
            else mudar({ estado: 'falhou', mensagem: nova.motivo ?? 'A ação não foi feita.' });
            fala = falaDoDesfecho(nova);
        } catch (e) {
            const erro = explicarErroDoBrain(e);
            mudar({ estado: 'falhou', mensagem: `${erro.titulo}. ${erro.detalhe}` });
            fala = `Não consegui. ${erro.titulo}.`;
        }
        if (fala) void opcoes.current.anunciar(fala);

        await esperar(DESFECHO_A_VISTA_MS);
        if (emFocoRef.current?.id === id && !emFocoRef.current.saindo) await tirarDeCena(id);
    }, [tirarDeCena]);

    const resolverFala = useCallback(async (texto: string, { comecouEm }: { comecouEm: number }) => {
        const alvo = emFocoRef.current;
        if (!alvo || alvo.estado !== 'aguardando' || alvo.saindo || opcoes.current.somenteLeitura) return false;
        /* Frase que começou antes de o cartão aparecer respondia a outra coisa. */
        if (Date.now() < alvo.visivelDesde || comecouEm < alvo.visivelDesde) return false;
        let intencao = intencaoSobreAcao(texto);
        if (intencao === 'incerta') intencao = await perguntarAoModeloRapido(texto, alvo.acao, opcoes.current.idioma);
        const ainda = emFocoRef.current;
        if (!ainda || ainda.id !== alvo.id || ainda.estado !== 'aguardando') return false;
        if (intencao !== 'confirmar' && intencao !== 'cancelar') return false;
        void decidir(alvo.id, intencao);
        return true;
    }, [decidir]);

    const confirmar = useCallback(() => {
        const alvo = emFocoRef.current;
        if (alvo) void decidir(alvo.id, 'confirmar');
    }, [decidir]);

    const cancelar = useCallback(() => {
        const alvo = emFocoRef.current;
        if (alvo) void decidir(alvo.id, 'cancelar');
    }, [decidir]);

    const dispensar = useCallback(() => {
        const alvo = emFocoRef.current;
        if (alvo && alvo.estado !== 'aguardando' && alvo.estado !== 'executando') void tirarDeCena(alvo.id);
    }, [tirarDeCena]);

    const atual = useMemo<InteracaoDaVoz | null>(() => {
        if (!emFoco || !ativa) return null;
        const ids = idsPendentes ? idsPendentes.split('|') : [];
        const indice = ids.indexOf(emFoco.id);
        return {
            acao: emFoco.acao,
            estado: emFoco.estado,
            decisao: emFoco.decisao,
            mensagem: emFoco.mensagem,
            posicao: indice < 0 ? 1 : indice + 1,
            total: indice < 0 ? ids.length + 1 : ids.length,
            visivel: emFoco.visivelDesde <= agora,
            saindo: emFoco.saindo,
        };
    }, [ativa, agora, emFoco, idsPendentes]);

    return { atual, confirmar, cancelar, dispensar, resolverFala };
}
